/**
 * `NewCampaignDialog` — TH-55143 (M-campT.5) + TH-55110 (slug-based clone).
 *
 * Modal de "Nova campanha" com dois caminhos:
 *   1. Em branco → callback `onCreateBlank()` (página decide pra onde
 *      mandar; hoje rota /campaigns/new).
 *   2. Usar template → POST `/api/campaigns/from-template/{slug}` →
 *      callback `onCloned(id)` recebe o id da campanha recém-criada.
 *      Aqui o clone é "quick": herda o nome do template. Para custom
 *      naming, o operador usa a galeria `/campaigns/templates` (TH-55110).
 *
 * Navegação fica no parent (CampaignsPage) — o dialog não importa
 * `useNavigate`. Isso mantém o teste do componente isolado do router
 * (Router só seria necessário se a navegação estivesse aqui dentro, e o
 * harness teria custo desproporcional pra uma feature de 1 SP).
 *
 * Não há toast lib global no admin; feedback de loading/erro fica
 * inline — mesmo padrão usado por `CustomArtUploadModal` com `submitError`.
 */

import { useState } from "react";
import { Plus, FileText } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  useCampaignTemplates,
  useCloneCampaignTemplate,
  type CampaignTemplateRead,
} from "@/hooks/useCampaignTemplates";
import { cn } from "@/lib/utils";

export interface NewCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado quando o operador escolhe começar do zero. */
  onCreateBlank: () => void;
  /** Chamado depois de um clone bem-sucedido com o id da campanha gerada. */
  onCloned: (campaignId: string) => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  classica: "Clássica",
  promocional: "Promocional",
  // Labels legados de departamento — ainda aparecem em mocks antigos.
  hortifruti: "Hortifrúti",
  acougue: "Açougue",
  padaria: "Padaria",
  mercearia: "Mercearia",
  bebidas: "Bebidas",
  promo: "Promo",
};

function categoryLabel(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}

export function NewCampaignDialog({
  open,
  onOpenChange,
  onCreateBlank,
  onCloned,
}: NewCampaignDialogProps) {
  const templatesQuery = useCampaignTemplates();
  const cloneMutation = useCloneCampaignTemplate();
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const disabled = cloneMutation.isPending;

  function handleBlank() {
    if (disabled) return;
    onCreateBlank();
    onOpenChange(false);
  }

  async function handleUseTemplate(tpl: CampaignTemplateRead) {
    if (disabled) return;
    setError(null);
    setActiveTemplateId(tpl.id);
    try {
      const result = await cloneMutation.mutateAsync({ slug: tpl.slug });
      onCloned(result.id);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao clonar template.");
    } finally {
      setActiveTemplateId(null);
    }
  }

  const templates = templatesQuery.data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogTitle>Nova campanha</DialogTitle>
        <DialogDescription>
          Comece do zero ou parta de um template pronto.
        </DialogDescription>

        <div className="mt-4 space-y-6">
          {/* Seção 1 — Em branco */}
          <section
            data-testid="new-campaign-section-blank"
            className={cn(
              "rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4",
              "flex items-center justify-between gap-4",
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--color-secondary)]">
                <Plus className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Em branco</h3>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Monte do zero. Você define telas, SKUs, layout e janela.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handleBlank}
              disabled={disabled}
              data-testid="new-campaign-blank-button"
            >
              Criar vazia
            </Button>
          </section>

          {/* Divisor */}
          <div
            className="flex items-center gap-3 text-xs text-[var(--color-muted-foreground)]"
            aria-hidden="true"
          >
            <span className="h-px flex-1 bg-[var(--color-border)]" />
            <span>ou</span>
            <span className="h-px flex-1 bg-[var(--color-border)]" />
          </div>

          {/* Seção 2 — Usar template */}
          <section data-testid="new-campaign-section-templates">
            <header className="mb-3">
              <h3 className="text-sm font-semibold">Usar template</h3>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Clona o template como rascunho — você ajusta os detalhes depois.
              </p>
            </header>

            {templatesQuery.isLoading && (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Carregando templates…
              </p>
            )}

            {templatesQuery.isError && (
              <p
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                Não foi possível listar templates: {templatesQuery.error.message}
              </p>
            )}

            {!templatesQuery.isLoading && templates.length === 0 && !templatesQuery.isError && (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Nenhum template disponível.
              </p>
            )}

            {templates.length > 0 && (
              <ul
                className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3"
                data-testid="new-campaign-template-grid"
              >
                {templates.map((tpl) => {
                  const isCloning = disabled && activeTemplateId === tpl.id;
                  return (
                    <li
                      key={tpl.id}
                      className={cn(
                        "flex flex-col gap-2 rounded-md border border-[var(--color-border)]",
                        "bg-[var(--color-card)] p-3",
                      )}
                      data-testid={`new-campaign-template-${tpl.id}`}
                    >
                      <div className="flex aspect-video items-center justify-center overflow-hidden rounded bg-[var(--color-secondary)]">
                        {tpl.thumb_url ? (
                          <img
                            src={tpl.thumb_url}
                            alt={`Pré-visualização do template ${tpl.name}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FileText
                            className="h-8 w-8 text-[var(--color-muted-foreground)]"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-medium leading-tight">
                          {tpl.name}
                        </h4>
                        <Badge variant="muted">
                          {categoryLabel(tpl.category)}
                        </Badge>
                      </div>
                      {tpl.description && (
                        <p className="text-xs text-[var(--color-muted-foreground)]">
                          {tpl.description}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleUseTemplate(tpl)}
                        disabled={disabled}
                        data-testid={`use-template-${tpl.id}`}
                      >
                        {isCloning ? "Clonando…" : "Usar este"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}

            {error && (
              <p
                role="alert"
                className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {error}
              </p>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
